import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { PluginListenerHandle } from '@capacitor/core';
import { ApplicationFacade, ChangePasswordFacade, CustomerFacade, NotificationFacade } from '@ridango/components';
import { passwordValidator } from '@ridango/core/lib/validators/password.validator';
import { take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';
import { ChangePasswordDTO } from '@ridango/core';

@Component({
    selector: 'rwidget-customer-account-change-password-v2',
    templateUrl: './customer-account-change-password-v2.component.html',
    styleUrls: ['./customer-account-change-password-v2.component.scss']
})
export class CustomerAccountChangePasswordV2Component implements OnInit, OnDestroy {
    @Output() backRouteEmit = new EventEmitter();
    @Output() saveSuccess = new EventEmitter();

    public assetsPath$ = this.applicationFacade.assetsPath$;
    public passwordForm: FormGroup;
    public showOldPassword = false;
    public showNewPassword = false;
    public showNewPasswordRepeat = false;

    private keyboardWillHideEvent: PluginListenerHandle;
    private keyboardWillShowEvent: PluginListenerHandle;

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private changePasswordFacade: ChangePasswordFacade,
        private customerFacade: CustomerFacade,
        private notification: NotificationFacade,
        private translate: TranslateService
    ) {}

    ngOnInit() {
        this.passwordForm = new FormGroup({
            username: new FormControl('', [Validators.required]),
            password: new FormControl('', [Validators.required]),
            newPassword: new FormControl('', passwordValidator),
            newPasswordAgain: new FormControl('', passwordValidator)
        });

        this.changePasswordFacade.getNewPassword();
        this.customerFacade.getCustomerProfile();
        this.customerFacade.profile$.subscribe((d) => this.passwordForm.controls['username'].patchValue(d.email));

        this.changePasswordFacade.isSubmitted$.pipe().subscribe((isSubmitted) => {
            if (isSubmitted) {
                this.changePasswordFacade.error$.pipe(take(1)).subscribe((error) => {
                    if (error) {
                        this.handleError(error);
                    } else {
                        this.notification.success(this.translate.instant('LOCAL_CHANGE_PASSWORD_SUCCESS'), {
                            showAfterRouteChange: true
                        });
                        this.saveSuccess.emit();
                    }
                });
            }
        });
    }

    ngOnDestroy() {
        this.keyboardWillHideEvent?.remove();
        this.keyboardWillShowEvent?.remove();
    }

    public handleError(e: any) {
        if (e.error.message) {
            this.notification.error(this.translate.instant(e.error.message));
        } else {
            this.notification.error(this.translate.instant(e.error));
        }
    }

    public submitPassword() {
        if (this.passwordForm.valid) {
            if (
                this.passwordForm.controls['newPassword'].value === this.passwordForm.controls['newPasswordAgain'].value
            ) {
                const payload: ChangePasswordDTO = {
                    ...this.passwordForm.value,
                    oauthClientId: this.env.WMB?.AUTH?.CLIENT_ID ?? null
                };
                delete payload['newPasswordAgain'];
                this.changePasswordFacade.setNewPassword(payload);
            } else {
                this.notification.error(this.translate.instant('LOCAL_PASSWORDS_MUST_MATCH'));
            }
        }
    }

    public getFirstError(control: AbstractControl, errorCodes: string[]): string {
        return errorCodes.find((errorCode) => control.hasError(errorCode));
    }

    public handleBackArrow() {
        if (this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE) {
            window.history.back();
        } else {
            this.backRouteEmit.emit();
        }
    }
}
